import React from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { useQuery } from "@apollo/client";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useAppSelector, useHistoryState } from "../../app/hooks";
import {
  GET_COLOR_ANALYSES,
  GET_COLOR_CLS,
  GET_COLOR_STONES,
  tokenOptions,
} from "../../backend/graphql";
import { backendURL } from "../../constants";
import ColorStateChips from "../widgets/ColorStateChips";
import PageTitle from "../widgets/PageTitle";
import SectionPaper from "../widgets/SectionPaper";
import SectionTitle from "../widgets/SectionTitle";
import SpreadBox, {
  SPREAD_AXES,
  SpreadAxis,
  unwrappedHue,
} from "../widgets/SpreadBox";
import ThumbnailCard from "../widgets/ThumbnailCard";
import useDocumentTitle from "../widgets/useDocumentTitle";
import QuickFilter from "../widgets/QuickFilter";
import { ColorSystem } from "../widgets/ColorSpread";
import { byColorSystemThenName, formatColorQuery } from "../../tools";

// A stone of the color, at the one color its analysis read.
type ColorStone = {
  id: string;
  sku: string;
  mainImageID: string | null;
  L: number;
  C: number;
  H: number;
  certified: boolean;
};

// What differs between the color systems the page shows: which stone field carries the color,
// whether that field is a number (a flat or a melee is a lot number), and whether a GIA
// certificate means anything for it — a Langerman color is the house's own grading and a flat or
// a melee a lot, so it does not, and they are not in the final grading either, which learns from
// the certificates.
type SystemSettings = {
  stoneColorField: string;
  isNumber: boolean;
  usesCertification: boolean;
};

const SYSTEMS: Partial<Record<ColorSystem, SystemSettings>> = {
  GIA: {
    stoneColorField: "color",
    isNumber: false,
    usesCertification: true,
  },
  Langerman: {
    stoneColorField: "langermanColor",
    isNumber: false,
    usesCertification: false,
  },
  Flat: {
    stoneColorField: "flat",
    isNumber: true,
    usesCertification: false,
  },
  Melee: {
    stoneColorField: "melee",
    isNumber: true,
    usesCertification: false,
  },
};

// How many stones are shown at each end of an axis.
const EXTREME_COUNT = 2;

// The fewest members a color's model needs for the color to be listed: one is enough, since the
// probable range stands in for a spread its few stones cannot give.
const MIN_STONES = 1;

// A share of stones, as a whole percentage; an em dash when there were none to count.
const percent = (share: number | null | undefined) =>
  share != null ? Math.round(share * 100) + "%" : null;

const gradingValue = (value: string | null) =>
  value != null ? (
    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  ) : (
    <Typography variant="body1" color="text.secondary">
      —
    </Typography>
  );

const THUMBNAIL_SIZE = 96;

// The color analysis page: one GIA, Langerman, flat or melee color at a time, chosen from the valid
// colors that have a model — named as Pricing by color names them — with how its members' colors
// spread, the stones at the ends of each axis and the members' histograms summed into one patch.
// The spread and the patch are the worker's, kept on the color's analysis; the stones are read
// live — for GIA, certified or all of them.
const ColorAnalysis = () => {
  const token = useAppSelector((state) => state.auth.token);
  const [searchParams, setSearchParams] = useSearchParams();
  // The color as the list names it: "Fancy Pink", "Langerman Yellow-Honey-1", "Flat 35241".
  const color = searchParams.get("color");
  useDocumentTitle(color, "Color analysis");
  const [certifiedOnly, setCertifiedOnly] = useHistoryState<boolean>(
    "colorAnalysisCertifiedOnly",
    true,
  );

  const systemFilter = (Object.keys(SYSTEMS) as ColorSystem[])
    .map((name) => 'system == "' + name + '"')
    .join(" || ");
  const { data: colorData } = useQuery(GET_COLOR_CLS, {
    ...tokenOptions(token),
    variables: {
      take: 10000,
      skip: 0,
      filter: "valid == true && (" + systemFilter + ")",
    },
  });
  // Only the colors with something to show: a model drawn by at least as many stones as a color is
  // shown with. Every valid color has an analysis row, many of them drawn by no stone. A GIA color
  // drawn only by uncertified stones still shows none with the certified filter on.
  const { data: analyzedData } = useQuery(GET_COLOR_ANALYSES, {
    ...tokenOptions(token),
    variables: {
      take: 10000,
      skip: 0,
      filter: "memberCount >= " + MIN_STONES,
    },
  });
  const analyzedColorIDs = new Set(
    (analyzedData?.colorAnalyses?.items ?? []).map((item: any) => item.colorID),
  );
  const colors = (colorData?.colorCls?.items ?? []).filter((item: any) =>
    analyzedColorIDs.has(item.id),
  );
  const selected = colors.find(
    (item: any) => formatColorQuery(item.system, item.color) === color,
  );
  const options = colors
    .map((item: any) => formatColorQuery(item.system, item.color))
    .sort(byColorSystemThenName)
    .map((name: string) => ({ label: name, value: name }));

  const system = (selected?.system ?? "GIA") as ColorSystem;
  const settings = SYSTEMS[system] ?? (SYSTEMS.GIA as SystemSettings);
  const certifiedShown = settings.usesCertification && certifiedOnly;

  const { data: analysisData } = useQuery(GET_COLOR_ANALYSES, {
    ...tokenOptions(token),
    skip: !selected,
    variables: {
      take: 1,
      skip: 0,
      filter: 'colorID == "' + selected?.id + '"',
    },
  });
  const analysis = analysisData?.colorAnalyses?.items[0];
  const percentiles = analysis?.percentiles
    ? JSON.parse(analysis.percentiles)
    : null;
  const grading = analysis?.grading ? JSON.parse(analysis.grading) : null;

  const certifiedFilter = certifiedShown ? " && isGIACertified == true" : "";
  const { data: stoneData } = useQuery(GET_COLOR_STONES, {
    ...tokenOptions(token),
    skip: !selected,
    variables: {
      take: 10000,
      skip: 0,
      filter:
        settings.stoneColorField +
        " == " +
        (settings.isNumber ? selected?.color : '"' + selected?.color + '"') +
        certifiedFilter,
    },
  });

  // The stones whose color was read, their hue on the same turn as the stored percentiles.
  const hueMedian = percentiles?.H?.[2] ?? 180;
  const stones: ColorStone[] = (stoneData?.diamonds?.items ?? [])
    .filter((stone: any) => stone.diamondAnalyses?.[0]?.oKL != null)
    .map((stone: any) => {
      const read = stone.diamondAnalyses[0];
      return {
        id: stone.id,
        sku: stone.sKU,
        mainImageID: stone.mainImageID,
        L: read.oKL,
        C: read.oKC,
        H: unwrappedHue(read.oKH, hueMedian),
        certified: stone.isGIACertified,
      };
    });

  // The stones at each end of an axis, never the same stone twice: with too few stones to fill
  // both ends, they are shared between the two, the lower half on the left.
  const extremes = (axis: SpreadAxis) => {
    const ordered = [...stones].sort((a, b) => a[axis.key] - b[axis.key]);
    const lowCount = Math.min(EXTREME_COUNT, Math.ceil(ordered.length / 2));
    const highCount = Math.min(EXTREME_COUNT, ordered.length - lowCount);
    const lowest = ordered.slice(0, lowCount);
    const highest = ordered.slice(ordered.length - highCount).reverse();
    return { lowest, highest };
  };

  const stoneCard = (stone: ColorStone) => (
    <Stack key={stone.id} alignItems="center" spacing={0.5}>
      <ThumbnailCard
        size={THUMBNAIL_SIZE}
        src={`${backendURL}/image/thumbnail/${stone.mainImageID}`}
        alt={stone.sku}
        href={`/analytics/fromdiamond/${stone.id}`}
      />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {stone.sku}
      </Typography>
    </Stack>
  );

  return (
    <SectionPaper>
      <PageTitle />
      <Stack spacing={2}>
        <Box>
          <Select
            isMulti={false}
            isClearable={false}
            name="colors"
            options={options}
            className="basic-multi-select"
            classNamePrefix="select"
            value={color ? [{ label: color, value: color }] : []}
            onChange={(choice: any) => setSearchParams({ color: choice.value })}
          />
          {!!color && settings.usesCertification && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <QuickFilter
                active={certifiedOnly}
                label="GIA certified"
                toggle={(active) => setCertifiedOnly(!active)}
              />
            </Stack>
          )}
        </Box>

        {/* The color's own figures — its spread, its probable range, its patch — are its members',
            whatever the filter; the filter only picks the stones shown among them. */}
        {!!color && analysis && (
          <>
            <SectionTitle>Grading</SectionTitle>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "max-content max-content",
                columnGap: 2,
                rowGap: 0.5,
                alignItems: "baseline",
              }}
            >
              {/* Every stone with an analyzed original draws its colors' models — for GIA, every
                  certified one — so the model's members are exactly the samples there are. */}
              <Typography variant="body2" color="text.secondary">
                Samples
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {settings.usesCertification
                  ? analysis.certifiedMemberCount
                  : analysis.memberCount}
              </Typography>
              {/* How well the final grading finds the color: the share of its own certified
                  stones, each graded with the color read without it, that had it in their first
                  three — and what that says of the color. Only GIA colors are graded. */}
              {settings.usesCertification && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Percentage in top 3
                  </Typography>
                  {gradingValue(
                    percent(
                      grading?.Certified
                        ? (grading.Certified - grading.NotInTop3) /
                            grading.Certified
                        : null,
                    ),
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Reliability
                  </Typography>
                  {grading?.State ? (
                    <ColorStateChips state={grading.State} />
                  ) : (
                    gradingValue(null)
                  )}
                </>
              )}
            </Box>

            <SectionTitle>Spread</SectionTitle>
            {percentiles ? (
              // Three plots across on a wide screen, fewer as it narrows: each keeps its own size.
              <Grid container spacing={2}>
                {SPREAD_AXES.map((axis) => (
                  <Grid item key={axis.key} xs={12} md={6} lg={4}>
                    <SpreadBox
                      label={axis.label}
                      percentiles={percentiles[axis.key]}
                      values={stones.map((stone) => stone[axis.key])}
                      // Where the color's range probably lies, as the worker estimated it from
                      // its members' spread.
                      outline={percentiles.ProbableRange?.[axis.key]}
                      format={axis.format}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}

            <SectionTitle>Extremes</SectionTitle>
            {stones.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No stone shown
              </Typography>
            )}
            {stones.length > 0 &&
              SPREAD_AXES.map((axis) => {
                const { lowest, highest } = extremes(axis);
                return (
                  <Stack key={axis.key} direction="row" spacing={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ width: 80 }}
                    >
                      {axis.label}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      {lowest.map((stone) => stoneCard(stone))}
                    </Stack>
                    {/* As tall as a thumbnail and at the top of the row, so the sign sits level with
                      the pictures' middle rather than with the whole cards, captions included. */}
                    {highest.length > 0 && (
                      <Box
                        sx={{
                          height: THUMBNAIL_SIZE,
                          display: "flex",
                          alignItems: "center",
                          alignSelf: "flex-start",
                        }}
                      >
                        <Typography variant="h6" color="text.secondary">
                          ⇔
                        </Typography>
                      </Box>
                    )}
                    <Stack direction="row" spacing={2}>
                      {highest.map((stone) => stoneCard(stone))}
                    </Stack>
                  </Stack>
                );
              })}

            <SectionTitle>Patch</SectionTitle>
            {analysis.colorPatchID ? (
              <img
                alt=""
                width={124}
                src={`${backendURL}/image/${analysis.colorPatchID}`}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
          </>
        )}
      </Stack>
    </SectionPaper>
  );
};

export default ColorAnalysis;
